import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { InputManager } from '@/utils/input_manager';
import type { AnyInputEvent, Binding } from '@/utils/input_manager';

const InputManagerContext = createContext<InputManager | null>(null);

export function InputProvider({ children, defaultBindings }: {
    children: React.ReactNode;
    defaultBindings?: Binding[];
}) {
    const [manager] = useState(() => {
        const m = new InputManager();
        if (defaultBindings?.length) m.setBindings(defaultBindings);
        return m;
    });

    useEffect(() => manager.attach(), [manager]);

    return (
        <InputManagerContext.Provider value={manager}>
            {children}
        </InputManagerContext.Provider>
    );
}

// Register a named action for the lifetime of the component.
// The callback is called when a binding targeting this action fires.
export function useAction(
    componentName: string,
    actionName: string,
    callback: () => void,
    deps: React.DependencyList = [],
): void {
    const manager = useContext(InputManagerContext);
    useEffect(() => {
        if (!manager) return;
        return manager.registerAction(componentName, actionName, callback);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manager, componentName, actionName, ...deps]);
}

type EventOfType<T extends AnyInputEvent['type']> = Extract<AnyInputEvent, { type: T }>;

interface UseInputEventOptions {
    // When provided, the handler only fires if the event's startTarget is inside this element.
    target?: { current: Element | null };
    // When true, scroll handlers also receive synthetic post-lift inertia events.
    // Default (false) filters them out so consumers see a clean end at finger lift.
    inertia?: boolean;
}

// Subscribe to specific input event types for the lifetime of the component.
// The callback is typed to the requested event type(s) — no manual type guard needed.
// Returning true from the handler calls preventDefault() + stopImmediatePropagation()
// on the originating DOM event (only meaningful for wheel-derived scroll events).
export function useInputEvent<T extends AnyInputEvent['type']>(type: T,   handler: (e: EventOfType<T>) => boolean | void, deps?: React.DependencyList, options?: UseInputEventOptions): void;
export function useInputEvent<T extends AnyInputEvent['type']>(type: T[], handler: (e: EventOfType<T>) => boolean | void, deps?: React.DependencyList, options?: UseInputEventOptions): void;
export function useInputEvent<T extends AnyInputEvent['type']>(
    type: T | T[],
    handler: (e: EventOfType<T>) => boolean | void,
    deps: React.DependencyList = [],
    options?: UseInputEventOptions,
): void {
    const manager = useContext(InputManagerContext);
    const optionsRef = useRef(options);
    optionsRef.current = options;
    useEffect(() => {
        if (!manager) return;
        const types = Array.isArray(type) ? type : [type];
        return manager.subscribe((e) => {
            if (!(types as AnyInputEvent['type'][]).includes(e.type)) return;
            const opts = optionsRef.current;
            if (opts?.target !== undefined) {
                const startTarget = (e as { startTarget?: Element | null }).startTarget;
                if (!opts.target.current?.contains(startTarget ?? null)) return;
            }
            if (!opts?.inertia && (e as { inertia?: true }).inertia) return;
            return handler(e as EventOfType<T>);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manager, ...deps]);
}

// Direct access to the InputManager instance for imperative use.
export function useInputManager(): InputManager | null {
    return useContext(InputManagerContext);
}
